function getObjectPolicy(grants, modelKey) {
  return grants?.objects?.[modelKey] || {};
}

/** Module enabled — type-level view/edit only (not view_all). */
function hasModuleViewCapability(grants, modelKey) {
  const policy = getObjectPolicy(grants, modelKey);
  return Boolean(policy.can_view || policy.can_update);
}

function hasModuleEditCapability(grants, modelKey) {
  return Boolean(getObjectPolicy(grants, modelKey).can_update);
}

function hasExplicitResourceGrant(entries = [], resourceId, wantsEdit) {
  const entry = entries.find((item) => String(item.id) === resourceId);
  if (!entry) return false;
  if (wantsEdit) return Boolean(entry.can_edit);
  return Boolean(entry.can_view || entry.can_edit);
}

function hasAccountScopedEdit(accounts, resourceId) {
  return hasExplicitResourceGrant(accounts, resourceId, true);
}

function hasCampaignScopedEdit({ campaigns, accounts, resourceId, accountId }) {
  if (hasExplicitResourceGrant(campaigns, resourceId, true)) {
    return true;
  }
  if (accountId) {
    return hasExplicitResourceGrant(accounts, String(accountId), true);
  }
  return accounts.some((account) => Boolean(account.can_edit));
}

function passesEditCapabilityGate({
  grants,
  wantsEdit,
  moduleEditKeys,
  hasScopedEdit,
}) {
  if (!wantsEdit) return true;

  const hasModuleEdit = moduleEditKeys.some((key) => hasModuleEditCapability(grants, key));
  return hasModuleEdit || hasScopedEdit;
}

/**
 * Business function / service instance access is scope-only.
 * Type-level can_view_all does not bypass the resource scope list.
 *
 * @param {import('./types/authz.types').AuthzContext} context
 * @param {{ type: 'account'|'campaign', id: string, action?: 'view'|'edit', accountId?: string }} params
 */
export function canAccessResource(context, { type, id, action = 'view', accountId }) {
  if (!context || !id) {
    return false;
  }

  if (context.isFullAccess) {
    return true;
  }

  if (!context.isAuthzV2Enabled) {
    return true;
  }

  const resources = context.grants.resources || { accounts: [], campaigns: [] };
  const resourceId = String(id);
  const wantsEdit = action === 'edit';
  const grants = context.grants || {};

  const accounts = resources.accounts || [];
  const campaigns = resources.campaigns || [];

  if (type === 'account') {
    if (!hasModuleViewCapability(grants, 'accountinfo')) {
      return false;
    }

    const hasScopedEdit = hasAccountScopedEdit(accounts, resourceId);
    if (!passesEditCapabilityGate({
      grants,
      wantsEdit,
      moduleEditKeys: ['accountinfo'],
      hasScopedEdit,
    })) {
      return false;
    }

    return hasExplicitResourceGrant(accounts, resourceId, wantsEdit);
  }

  if (type === 'campaign') {
    const hasBs = hasModuleViewCapability(grants, 'proposalinfo');
    const hasBf = hasModuleViewCapability(grants, 'accountinfo');
    if (!hasBs && !hasBf) {
      return false;
    }

    const hasScopedEdit = hasCampaignScopedEdit({
      campaigns,
      accounts,
      resourceId,
      accountId,
    });
    if (!passesEditCapabilityGate({
      grants,
      wantsEdit,
      moduleEditKeys: ['proposalinfo', 'accountinfo'],
      hasScopedEdit,
    })) {
      return false;
    }

    if (hasExplicitResourceGrant(campaigns, resourceId, wantsEdit)) {
      return true;
    }

    if (accountId) {
      return hasExplicitResourceGrant(accounts, String(accountId), wantsEdit);
    }

    return accounts.some((account) => {
      if (wantsEdit) return Boolean(account.can_edit);
      return Boolean(account.can_view || account.can_edit);
    });
  }

  return false;
}

/**
 * Whether the profile can access at least one business function instance.
 * @param {import('./types/authz.types').AuthzContext} context
 */
export function canAccessAnyBusinessFunction(context) {
  if (!context) return false;
  if (context.isFullAccess || !context.isAuthzV2Enabled) {
    return true;
  }

  const grants = context.grants || {};
  if (!hasModuleViewCapability(grants, 'accountinfo')) {
    return false;
  }

  const accounts = grants.resources?.accounts || [];
  const campaigns = grants.resources?.campaigns || [];

  const hasAccountGrant = accounts.some((entry) => Boolean(entry.can_view || entry.can_edit));
  const hasCampaignGrant = campaigns.some((entry) => Boolean(entry.can_view || entry.can_edit));

  return hasAccountGrant || hasCampaignGrant;
}
